import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import DailySlotsAPIProvider from './DailySlotsAPIProvider';
import WeeklySlotsAPIProvider from './WeeklySlotsAPIProvider';
import { BookingSlot } from '../../../api/search';
import { ChosenSlots } from '../../../contexts/checkout';
import useLocationSlotsQuery from '../../../hooks/useLocationSlotsQuery';
import { mapKidsWithEligibility } from '../../../utils/children';
import { dateStringToDate } from '../../../utils/dateStringToDate';
import { CheckoutKid, useCheckoutKidsInfo } from '../../state/checkoutKids';

const SlotsFetchingContext = createContext(false);
export const useIsSlotsFetching = () => useContext(SlotsFetchingContext);

const KidsWithEligibilityContext = createContext<CheckoutKid[]>([]);
export const useKidsWithEligibility = () =>
  useContext(KidsWithEligibilityContext);

export default function SlotsAPIProvider({
  locationId,
  children,
  initialDailySlots,
  initialWeeklySlots,
}: {
  locationId: number;
  children: React.ReactNode;
  initialDailySlots?: ChosenSlots;
  initialWeeklySlots?: Record<string, string[]>;
}) {
  const slotsQueryResults = useLocationSlotsQuery(locationId);
  const { data: slots, isFetching: fetching } = slotsQueryResults;

  const kidsInfo = useCheckoutKidsInfo();
  const kidsWithEligibility = useMemo(
    () => mapKidsWithEligibility(kidsInfo, slots),
    [kidsInfo, slots]
  );

  return (
    <CurrentVisibleDateProvider slotsQueryResults={slotsQueryResults}>
      <SlotsFetchingContext.Provider value={fetching}>
        <KidsWithEligibilityContext.Provider value={kidsWithEligibility}>
          <DailySlotsAPIProvider
            slots={slots}
            kids={kidsWithEligibility}
            initialState={initialDailySlots}
          >
            <WeeklySlotsAPIProvider
              slots={slots}
              kids={kidsWithEligibility}
              initialState={initialWeeklySlots}
            >
              {children}
            </WeeklySlotsAPIProvider>
          </DailySlotsAPIProvider>
        </KidsWithEligibilityContext.Provider>
      </SlotsFetchingContext.Provider>
    </CurrentVisibleDateProvider>
  );
}

const CurrentVisibleDateContext = createContext<Date | undefined>(undefined);
export const useCurrentVisibleDate = () =>
  useContext(CurrentVisibleDateContext);
const CurrentVisibleDateSetterContext = createContext((d: Date) => {});
export const useSetCurrentVisibleDate = () =>
  useContext(CurrentVisibleDateSetterContext);

function CurrentVisibleDateProvider({
  children,
  slotsQueryResults,
}: {
  children: React.ReactNode;
  slotsQueryResults: {
    readonly data: BookingSlot[];
    readonly isFetching: boolean;
    readonly firstSlotDate: string | undefined;
  };
}) {
  const today = new Date();
  const todayPrefix = `${today.toLocaleDateString(undefined, {
    year: 'numeric',
  })}-${today.toLocaleDateString(undefined, {
    month: '2-digit',
  })}`;

  const [syncedDate, setSyncedDate] = useState(today);

  useEffect(() => {
    if (slotsQueryResults.isFetching) return;
    const { firstSlotDate } = slotsQueryResults;

    if (!firstSlotDate) return;
    const d = dateStringToDate(firstSlotDate);

    if (d >= today && !firstSlotDate.startsWith(todayPrefix)) {
      setSyncedDate(d);
    }
  }, [slotsQueryResults]);

  return (
    <CurrentVisibleDateContext.Provider value={syncedDate}>
      <CurrentVisibleDateSetterContext.Provider value={setSyncedDate}>
        {children}
      </CurrentVisibleDateSetterContext.Provider>
    </CurrentVisibleDateContext.Provider>
  );
}
